import { useState } from 'react'

import { ChevronDown } from 'lucide-react'

import { ATTENTION_BANDS } from '../lib/attention'
import { LENSES, LENS_NO_DATA, SITE_STATUS_META } from '../lib/lens'

import type { GisLens } from '../api/types'
import type { MapMetric } from '../layers/mandalChoroplethLayer'

interface MapLegendProps {
  lens: GisLens
  /** What the choropleth shades by — attention (Overview/Issues) or the coverage/officer %. */
  metric: MapMetric
  /** Show the issue-hotspot ring legend when the overlay layer is on. */
  issuesOverlay?: boolean
}

const ISSUE_RINGS: readonly (readonly [string, string])[] = [
  ['Critical', '#dc2626'],
  ['Open', '#f59e0b'],
]

// Reverse ONCE; build both the swatch bar and the labels from the same source so they cannot desync
// (spec §4.5). Stored order is dark→light; reversed gives the mock's light→dark, labels 0 → 35+.
const ATT = [...ATTENTION_BANDS].reverse()

export function MapLegend({ lens, metric, issuesOverlay }: MapLegendProps) {
  const [collapsed, setCollapsed] = useState(false)
  const isAttention = metric === 'attention'
  const heading = isAttention ? 'Attention · fixed bands' : `${LENSES[lens].label} · fixed bands`
  // %-metric (coverage/officers): discrete semantic bands — render verbatim, never a 0/50/100 axis.
  const pctRows = LENSES[lens].legend

  return (
    <div className="absolute left-[18px] bottom-[18px] z-20 w-[236px] overflow-hidden rounded-[14px] bg-gis-card shadow-gis-pop ring-1 ring-[rgba(15,31,61,.06)]">
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
        className="flex w-full items-center justify-between px-[14px] py-[10px] text-left"
      >
        <b className="text-[12px] font-bold tracking-wide text-gis-ink uppercase">Legend</b>
        <ChevronDown
          className={`h-4 w-4 text-gis-muted transition-transform ${collapsed ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {!collapsed && (
        <div className="px-[14px] pb-[12px]">
          <div className="border-t border-[#f1f3f8] pt-[10px]">
            <h4 className="mb-2 text-[11px] font-bold tracking-wide text-gis-muted uppercase">{heading}</h4>
            {isAttention ? (
              <>
                <div className="flex h-[10px] overflow-hidden rounded-full">
                  {ATT.map((b) => (
                    <span key={b.label} className="flex-1" style={{ background: b.color }} />
                  ))}
                </div>
                <div className="mt-1.5 flex justify-between text-[10px] font-semibold text-gis-muted tabular-nums">
                  {ATT.map((b) => (
                    <span key={b.label}>{b.label}</span>
                  ))}
                </div>
              </>
            ) : (
              <>
                {pctRows.map(([label, color]) => (
                  <div key={label} className="my-1 flex items-center gap-2 text-xs text-gis-ink">
                    <span className="h-3.5 w-3.5 rounded border border-black/10" style={{ background: color }} />
                    {label}
                  </div>
                ))}
                {/* Issues map: 0 matching issues shades grey, distinct from the 0-5 red band. */}
                {metric === 'issues' && (
                  <div className="my-1 flex items-center gap-2 text-xs text-gis-ink">
                    <span className="h-3.5 w-3.5 rounded border border-black/10" style={{ background: LENS_NO_DATA }} />
                    No matching issues
                  </div>
                )}
              </>
            )}
          </div>
          <div className="mt-2 border-t border-[#f1f3f8] pt-[10px]">
            <h4 className="mb-1 text-[11px] font-bold tracking-wide text-gis-muted uppercase">Sites</h4>
            {Object.entries(SITE_STATUS_META).map(([key, meta]) => (
              <div key={key} className="my-1 flex items-center gap-2 text-xs text-gis-ink">
                <span className="h-3.5 w-3.5 rounded-full border border-black/10" style={{ background: meta.color }} />
                {meta.label}
              </div>
            ))}
          </div>
          {issuesOverlay && (
            <div className="mt-2 border-t border-[#f1f3f8] pt-[10px]">
              <h4 className="mb-1 text-[11px] font-bold tracking-wide text-gis-muted uppercase">Issue hotspots</h4>
              {ISSUE_RINGS.map(([label, color]) => (
                <div key={label} className="my-1 flex items-center gap-2 text-xs text-gis-ink">
                  <span className="h-3.5 w-3.5 rounded-full border-2 bg-transparent" style={{ borderColor: color }} />
                  {label}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
